import {
  type CollisionDetector,
  CollisionPriority,
  CollisionType,
  type UniqueIdentifier,
} from '@dnd-kit/abstract';
import { closestCorners } from '@dnd-kit/collision';

import { readRecord } from '@/lib/prop-utils';
import { getMidpointImpact } from './midpoint-impact';
import {
  createMovementIntervalTracker,
  MOVEMENT_INTERVAL_SENSITIVITY_PX,
} from './movement-interval';

type Direction = 'up' | 'down' | 'left' | 'right';

interface DynamicCollisionOptions {
  midpointOffset?: number;
}

type DropData = {
  type?: 'insert-gap-zone' | 'group-gap-zone' | 'parent-drop' | 'block';
  parentId?: string | null;
};

const getSourceParentId = (
  source: { data?: { current?: unknown } } | null | undefined,
): string | null => {
  const record = readRecord(source?.data?.current);
  return typeof record?.parentId === 'string' ? record.parentId : null;
};

const getDroppableData = (droppable: { data?: { current?: unknown } } | undefined): DropData => {
  const record = readRecord(droppable?.data?.current);
  return (record as DropData | undefined) ?? {};
};

const toRectLike = (shape: {
  boundingRectangle: { left: number; top: number; width: number; height: number };
}) => ({
  left: shape.boundingRectangle.left,
  top: shape.boundingRectangle.top,
  width: shape.boundingRectangle.width,
  height: shape.boundingRectangle.height,
});

const intersectsOnBothAxes = ({
  dragRect,
  dropRect,
}: {
  dragRect: { left: number; top: number; right: number; bottom: number };
  dropRect: { left: number; top: number; right: number; bottom: number };
}): boolean => {
  const xAxisIntersection = dropRect.right > dragRect.left && dropRect.left < dragRect.right;
  const yAxisIntersection = dropRect.bottom > dragRect.top && dropRect.top < dragRect.bottom;
  return xAxisIntersection && yAxisIntersection;
};
const movementTrackerByAxis = {
  both: createMovementIntervalTracker({
    axis: 'both',
    sensitivityPx: MOVEMENT_INTERVAL_SENSITIVITY_PX,
  }),
};

/**
 * Clears per-source collision direction state.
 * Call this at drag lifecycle boundaries to avoid stale direction carryover.
 */
export const resetDynamicCollisionSession = (sourceId?: UniqueIdentifier): void => {
  movementTrackerByAxis.both.reset(sourceId);
};

export const createDynamicCollisionDetector = ({
  midpointOffset = 0.05,
}: DynamicCollisionOptions = {}): CollisionDetector => {
  return ((input) => {
    const { dragOperation, droppable } = input;
    const source = dragOperation.source;
    const sourceId = source?.id;
    const sourceParentId = getSourceParentId(source);
    const dragShape = dragOperation.shape?.current;
    const dropShape = droppable.shape;
    const position = dragOperation.position.current;

    if (!sourceId || !dragShape || !dropShape || !position) {
      return null;
    }

    const direction = movementTrackerByAxis.both.track(sourceId, position) as Direction | null;
    const intersectionArea = dragShape.intersectionArea(dropShape);
    const intersectionRatio = intersectionArea / dropShape.area;

    // Sticky self-collision prevents abrupt target swaps during layout shifts.
    if (sourceId === droppable.id) {
      return {
        id: droppable.id,
        value: Number.isFinite(intersectionRatio) ? intersectionRatio : 0,
        priority: CollisionPriority.Highest,
        type: CollisionType.Collision,
      };
    }

    if (direction && intersectionArea > 0) {
      const overMidpoint = getMidpointImpact({
        dragRect: toRectLike(dragShape),
        dropRect: toRectLike(dropShape),
        direction,
        offsetMultiplier: midpointOffset,
      });

      if (overMidpoint) {
        return {
          id: droppable.id,
          value: Number.isFinite(intersectionRatio) ? intersectionRatio : 0,
          priority: CollisionPriority.High,
          type: CollisionType.Collision,
          data: { direction },
        };
      }
    }

    const droppableData = getDroppableData(droppable);
    const droppableParentId =
      typeof droppableData.parentId === 'string' ? droppableData.parentId : null;
    const isContainerLikeTarget =
      droppableData.type === 'insert-gap-zone' ||
      droppableData.type === 'group-gap-zone' ||
      droppableData.type === 'parent-drop';

    // In "both" mode, avoid broad closest-corners fallback across sibling containers
    // when we can preserve continuity within the current parent lane.
    if (
      isContainerLikeTarget &&
      sourceParentId &&
      droppableParentId &&
      sourceParentId !== droppableParentId
    ) {
      return null;
    }

    if (
      !intersectsOnBothAxes({
        dragRect: dragShape.boundingRectangle,
        dropRect: dropShape.boundingRectangle,
      })
    ) {
      return null;
    }

    const fallbackCollision = closestCorners(input);
    if (!fallbackCollision) {
      return null;
    }

    return {
      ...fallbackCollision,
      priority: intersectionArea > 0 ? CollisionPriority.Low : CollisionPriority.Lowest,
      data: { direction },
    };
  }) as CollisionDetector;
};
